import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { api } from '../utils/api';
import { useToast } from '../context/ToastContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTimes } from '@fortawesome/free-solid-svg-icons';
import ProvisionDevicePage from '../pages/ProvisionDevicePage';

const ProvisionDevice = ({ roomId, onDeviceProvisioned }) => {
        const [isLoading, setIsLoading] = useState(false);
        const [isOverlayOpen, setIsOverlayOpen] = useState(false);
        const [provisionToken, setProvisionToken] = useState(null);
        const { showToast } = useToast();
        const navigate = useNavigate();

        const openProvisioningOverlay = async () => {
                setIsLoading(true);
                try {
                        const response = await api('/devices/provision', {
                                method: 'POST',
                                body: JSON.stringify({ room_id: roomId }),
                        });

                        if (!response.ok) {
                                throw new Error('Failed to initiate device pairing');
                        }

                        const data = await response.json();
                        const token = data.data?.token;

                        if (!token) {
                                throw new Error('No pairing token received');
                        }

                        setProvisionToken(token);
                        setIsOverlayOpen(true);
                } catch (error) {
                        showToast('error', 'Failed to initiate device pairing');
                } finally {
                        setIsLoading(false);
                }
        };

        const handleProvisioningComplete = async (result) => {
                setIsOverlayOpen(false);
                setProvisionToken(null);

                if (result?.success) {
                        // Success - fetch device ID and navigate to device page
                        try {
                                const response = await api(`/devices/provision/${result.provision}`, {
                                        method: 'GET',
                                });
                                if (response.ok) {
                                        const data = await response.json();
                                        const deviceId = data.data?.device_id;

                                        showToast('success', 'Device successfully added');

                                        if (onDeviceProvisioned) {
                                                onDeviceProvisioned({ timestamp: new Date().toISOString() });
                                        }

                                        if (deviceId) {
                                                navigate(`/device/${deviceId}`);
                                        }
                                }
                        } catch (error) {
                                console.error('Error fetching device:', error);
                                showToast('success', 'Device added - check your dashboard');
                        }
                } else {
                        // Failure - navigate to troubleshooting
                        navigate('/troubleshooting', { state: { roomId, provision: result?.provision } });
                }
        };

        const handleCloseOverlay = () => {
                setIsOverlayOpen(false);
                setProvisionToken(null);
        };

        return (
                <>
                        <button
                                onClick={openProvisioningOverlay}
                                data-testid="provision-open"
                                disabled={isLoading || isOverlayOpen}
                                className="p-2 text-gray-400 hover:text-green-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                title={isLoading ? 'Initiating...' : isOverlayOpen ? 'Configuring...' : 'Add device'}
                        >
                                <FontAwesomeIcon icon={faPlus} className="w-4 h-4" />
                        </button>

                        {/* Provisioning Overlay */}
                        {isOverlayOpen && provisionToken && ReactDOM.createPortal(
                                <div data-testid="provision-overlay" className="fixed inset-0 bg-black/50 z-[5000] flex items-center justify-center p-4">
                                        <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[800px] overflow-auto relative">
                                                <button
                                                        onClick={handleCloseOverlay}
                                                        data-testid="provision-close"
                                                        className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 z-10"
                                                        title="Close"
                                                >
                                                        <FontAwesomeIcon icon={faTimes} className="w-5 h-5" />
                                                </button>
                                                <div className="p-8">
                                                        <ProvisionDevicePage
                                                                provisionToken={provisionToken}
                                                                onComplete={handleProvisioningComplete}
                                                                isOverlay={true}
                                                        />
                                                </div>
                                        </div>
                                </div>,
                                document.body
                        )}
                </>
        );
};

export default ProvisionDevice;
